import { FC, useCallback, useEffect, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { QUOTE_EMAILS_BY_QUOTE } from "../../Services/Queries/Queries";
import { SEND_QUOTE_UPLOAD_REQUEST_EMAIL } from "../../Services/Mutations/Mutations";
import { catchNotification, setNotification } from "../../../../helpers/cache";
import { LoadingDesign } from "../../../../helpers";
import { convertDateToUSFormat } from "../../../../helpers/utils";
import ActionButton from "../../../../components/Buttons/ActionButton";

interface QuoteEmailListItem {
  id: number;
  subject: string;
  to: string;
  status: number;
  emailType: string;
  text: string | null;
  sendDate: string | null;
  createdAt: string;
}

const emailStatusLabels: {
  [key: number]: { label: string; className: string };
} = {
  0: { label: "Pending", className: "bg-[#FFB752] text-white" },
  1: { label: "Sent", className: "bg-[#87B87F] text-white" },
  3: { label: "Error", className: "bg-[#D15B47] text-white" },
};

const ConfirmSendModal: FC<{
  onClose: () => void;
  onSend: () => void;
  loading: boolean;
}> = ({ onClose, onSend, loading }) => {
  return (
    <div className="fixed inset-0 bg-transparent z-50 p-2 md:flex md:flex-row md:justify-center md:items-center">
      <div
        className="fixed inset-0 bg-black/60 -z-20"
        onClick={() => {
          if (!loading) {
            onClose();
          }
        }}
      />
      <div className="w-[300px] sm:w-[560px] bg-[#e4e6e9] shadow-[0_0_64px_#000] mx-auto h-fit rounded-b-[6px]">
        <h3 className="bg-[#0078AE] max-w-[600px] text-[#000] font-semibold flex justify-between py-2 px-2">
          <span className="text-[16px] leading-5">
            Send upload request email
          </span>
          <span className="inline-flex justify-center items-center text-red-600">
            <i
              onClick={() => {
                if (!loading) {
                  onClose();
                }
              }}
              className={`icon-cancel text-white ${
                loading ? "cursor-not-allowed" : "cursor-pointer"
              }`}
            />
          </span>
        </h3>
        <p className="font-semibold text-sm text-center p-6">
          Send the file upload request email to the customer?
        </p>
        <div className="flex justify-center gap-4 p-3">
          <ActionButton
            buttonName="Cancel"
            className={`${
              loading ? "cursor-not-allowed" : "hover:bg-[#888] cursor-pointer"
            } bg-[#A0A0A0] border-[#A0A0A0] whitespace-nowrap`}
            buttonType="button"
            onClick={() => {
              if (!loading) {
                onClose();
              }
            }}
          />
          <ActionButton
            buttonName="Send"
            buttonType="button"
            className={`${
              loading
                ? "bg-[#a3c69d] border-[#a3c69d] cursor-not-allowed"
                : "bg-[#87B87F] border-[#87B87F] hover:bg-[#629B58]"
            }`}
            onClick={() => {
              if (!loading) {
                onSend();
              }
            }}
            icon={loading ? "icon-spin4" : "icon-mail"}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};

const EmailPreviewModal: FC<{
  email: QuoteEmailListItem;
  onClose: () => void;
}> = ({ email, onClose }) => {
  return (
    <div className="fixed inset-0 bg-transparent z-50 p-2 md:flex md:flex-row md:justify-center md:items-center">
      <div className="fixed inset-0 bg-black/60 -z-20" onClick={onClose} />
      <div className="w-[95vw] sm:w-[680px] bg-[#e4e6e9] shadow-[0_0_64px_#000] mx-auto h-fit rounded-b-[6px]">
        <h3 className="bg-[#0078AE] text-[#000] font-semibold flex justify-between py-2 px-2">
          <span className="text-[16px] leading-5">Email Preview</span>
          <span className="inline-flex justify-center items-center">
            <i
              onClick={onClose}
              className="icon-cancel text-white cursor-pointer"
            />
          </span>
        </h3>
        <div className="p-3 flex flex-col gap-2">
          <div className="text-sm">
            <div>
              <span className="font-semibold">Subject:</span> {email.subject}
            </div>
            <div>
              <span className="font-semibold">To:</span> {email.to}
            </div>
          </div>
          {email.text ? (
            <iframe
              title="Email preview"
              srcDoc={email.text}
              className="w-full h-[60vh] bg-white border border-[#c8c8c8] rounded"
            />
          ) : (
            <p className="text-sm p-4 text-center">
              No content available for this email.
            </p>
          )}
        </div>
        <div className="flex justify-center gap-4 p-3 pt-0">
          <ActionButton
            buttonName="Close"
            className="bg-[#A0A0A0] border-[#A0A0A0] hover:bg-[#888] cursor-pointer whitespace-nowrap"
            buttonType="button"
            onClick={onClose}
          />
        </div>
      </div>
    </div>
  );
};

const EditQuoteUploadRequestEmail: FC<{
  quoteId?: number;
  canSend: boolean;
}> = ({ quoteId, canSend }) => {
  const [emailsList, setEmailsList] = useState<QuoteEmailListItem[]>([]);
  const [isLoadingEmails, setIsLoadingEmails] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [previewEmail, setPreviewEmail] = useState<QuoteEmailListItem>();

  const [getQuoteEmailsByQuote] = useLazyQuery(QUOTE_EMAILS_BY_QUOTE, {
    fetchPolicy: "no-cache",
    onError: (error) => {
      setNotification([
        {
          type: "ERROR",
          message: error?.message || "Error, please try again later",
        },
      ]);
    },
  });

  const [sendQuoteUploadRequestEmail] = useMutation(
    SEND_QUOTE_UPLOAD_REQUEST_EMAIL,
  );

  const onFetchEmails = useCallback(() => {
    if (!quoteId) {
      return;
    }

    setIsLoadingEmails(true);

    getQuoteEmailsByQuote({
      variables: { quoteId, emailType: "upload_request" },
    })
      .then(({ data }) => {
        setEmailsList(data?.quoteEmailsByQuote || []);
      })
      .finally(() => {
        setIsLoadingEmails(false);
      });
  }, [getQuoteEmailsByQuote, quoteId]);

  useEffect(() => {
    if (quoteId) {
      onFetchEmails();
    }
  }, [quoteId, onFetchEmails]);

  const onSend = () => {
    if (!quoteId) {
      return;
    }

    setIsSending(true);

    sendQuoteUploadRequestEmail({ variables: { quoteId } })
      .then(() => {
        setNotification([
          {
            type: "SUCCESS",
            message:
              "The upload request email was queued and will be sent to the customer shortly",
          },
        ]);
        setShowConfirmModal(false);
        onFetchEmails();
      })
      .catch(catchNotification)
      .finally(() => {
        setIsSending(false);
      });
  };

  return (
    <>
      <div className="edit-quote__misc-card-header">
        <i className="icon-mail pr-1" />
        Request Data Files
      </div>
      <div className="edit-quote__misc-card-content">
        {isLoadingEmails && <LoadingDesign smallLoading className="h-max" />}
        {!isLoadingEmails && (
          <div className="flex flex-col gap-2">
            <div>
              <button
                className="btn btn-success btn-small"
                disabled={!canSend || !quoteId}
                onClick={() => {
                  setShowConfirmModal(true);
                }}
              >
                <i className="icon-mail pr-1" />
                Send Upload Request Email
              </button>
            </div>
            {!emailsList.length && (
              <p className="text-sm">No upload request emails sent yet.</p>
            )}
            {!!emailsList.length && (
              <ul className="flex flex-col">
                {emailsList.map((email) => (
                  <li
                    key={email.id}
                    role="button"
                    title="Click to preview the email content"
                    onClick={() => setPreviewEmail(email)}
                    className="flex items-center justify-between gap-2 py-1 border-b border-[#e8e8e8] last:border-b-0 text-sm cursor-pointer hover:bg-black/5"
                  >
                    <span className="break-all">
                      {convertDateToUSFormat(email.sendDate || email.createdAt)}{" "}
                      to {email.to}
                    </span>
                    <span
                      className={`px-2 py-[1px] rounded text-xs whitespace-nowrap ${
                        (
                          emailStatusLabels[email.status] ||
                          emailStatusLabels[0]
                        ).className
                      }`}
                    >
                      {
                        (
                          emailStatusLabels[email.status] ||
                          emailStatusLabels[0]
                        ).label
                      }
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      {showConfirmModal && (
        <ConfirmSendModal
          onClose={() => setShowConfirmModal(false)}
          onSend={onSend}
          loading={isSending}
        />
      )}
      {!!previewEmail && (
        <EmailPreviewModal
          email={previewEmail}
          onClose={() => setPreviewEmail(undefined)}
        />
      )}
    </>
  );
};

export default EditQuoteUploadRequestEmail;
